import {
  IMonthNum,
  ISeasonMapping,
  ISeasonalNum,
  ITimeDataItem,
} from './algoInterface'
import { IMonthNumPlusIMonthNum } from './calExCharge'
import { calMonthSum, multiplyValuesByRealTime } from './classifyTimeData'
import { calSeasonalSum } from './monthToSeason'

export type PvRevenueContractType =
  | 'kepcoPpa'
  | 'marketSmp'
  | 'fixedPriceContract'
  | 'directPpa'

export type PvSmpOpt = 'realTime' | 'min' | 'max' | 'weightedAver'
export type PvRecOpt = 'energyOnly' | 'energyAndRec'
export type PvRecRegion = 'land' | 'jeju'

type PvCustomerForRevenue = {
  facilityCapacity?: number
  previousMethod?: string
  revenueContractType?: string
  contractPrice?: number
  RECWeight?: number
}

const CONTRACT_TYPES: PvRevenueContractType[] = [
  'kepcoPpa',
  'marketSmp',
  'fixedPriceContract',
  'directPpa',
]

export const EMPTY_SEASONAL_NUM: ISeasonalNum = {
  spr: 0,
  sum: 0,
  fall: 0,
  win: 0,
}

export const EMPTY_MONTH_NUM: IMonthNum = {
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: 0,
  10: 0,
  11: 0,
  12: 0,
}

export const getPvRevenueContractType = (
  customer?: PvCustomerForRevenue
): PvRevenueContractType => {
  const type = customer?.revenueContractType
  if (type && CONTRACT_TYPES.includes(type as PvRevenueContractType)) {
    return type as PvRevenueContractType
  }

  if (customer?.previousMethod === 'fixedPrice') {
    return 'fixedPriceContract'
  }

  const facilityCapacity = Number(customer?.facilityCapacity ?? 0)
  return facilityCapacity > 1000 ? 'marketSmp' : 'kepcoPpa'
}

export const getPreviousMethodFromRevenueContractType = (
  revenueContractType: PvRevenueContractType
): 'fixedPrice' | 'market' =>
  revenueContractType === 'fixedPriceContract' ||
  revenueContractType === 'directPpa'
    ? 'fixedPrice'
    : 'market'

export const isUnitPriceRevenueContract = (
  revenueContractType: PvRevenueContractType
): boolean =>
  revenueContractType === 'fixedPriceContract' ||
  revenueContractType === 'directPpa'

export const isSmpRevenueContract = (
  revenueContractType: PvRevenueContractType
): boolean =>
  revenueContractType === 'kepcoPpa' || revenueContractType === 'marketSmp'

export const isSpotRecRevenueContract = (
  revenueContractType: PvRevenueContractType
): boolean =>
  revenueContractType === 'kepcoPpa' || revenueContractType === 'marketSmp'

export const getRevenueContractLabel = (
  revenueContractType: PvRevenueContractType
): string => {
  switch (revenueContractType) {
    case 'kepcoPpa':
      return '한전 PPA(SMP)'
    case 'marketSmp':
      return '전력시장 직접 판매(SMP)'
    case 'directPpa':
      return '제3자 PPA'
    case 'fixedPriceContract':
    default:
      return 'RPS 고정가격계약'
  }
}

export const getRevenueContractUnitPriceLabel = (
  revenueContractType: PvRevenueContractType
): string =>
  revenueContractType === 'directPpa' ? '협의 단가' : 'SMP+REC 고정단가'

export const normalizeSmpOptForPvContract = (
  revenueContractType: PvRevenueContractType,
  smpOpt?: string
): PvSmpOpt => {
  if (revenueContractType === 'kepcoPpa') return 'weightedAver'

  return (smpOpt as PvSmpOpt) || 'realTime'
}

export const normalizeRecOptForPvContract = (
  _revenueContractType: PvRevenueContractType,
  recOpt?: string
): PvRecOpt => (recOpt === 'energyAndRec' ? 'energyAndRec' : 'energyOnly')

const getPreviousRevenueRecOpt = (
  revenueContractType: PvRevenueContractType
): PvRecOpt =>
  isSpotRecRevenueContract(revenueContractType) ? 'energyAndRec' : 'energyOnly'

export const mapSmpDataToValue = (
  smpData: any[] = [],
  smpOpt: PvSmpOpt
): ITimeDataItem[] =>
  smpData.map((item: any) => {
    let selectedValue = item.value ?? 0

    switch (smpOpt) {
      case 'min':
        selectedValue = item.minSmp ?? 0
        break
      case 'max':
        selectedValue = item.maxSmp ?? 0
        break
      case 'weightedAver':
        selectedValue = item.weightedAver ?? 0
        break
      case 'realTime':
      default:
        selectedValue = item.value ?? 0
        break
    }

    return {
      date: item.date,
      value: Number(selectedValue) || 0,
    }
  })

const calculateSeasonalSpotRecRevenue = (
  customer: PvCustomerForRevenue | undefined,
  seasonalPVSum: ISeasonalNum,
  recOpt?: string,
  recYear?: string | number,
  seasonalRecAveragePrice: ISeasonalNum = EMPTY_SEASONAL_NUM
): ISeasonalNum => {
  if (recOpt !== 'energyAndRec' || !recYear) return { ...EMPTY_SEASONAL_NUM }

  const recWeight = Number(customer?.RECWeight ?? 1)

  return {
    spr: Math.round(
      (seasonalRecAveragePrice.spr * seasonalPVSum.spr * recWeight) / 1000
    ),
    sum: Math.round(
      (seasonalRecAveragePrice.sum * seasonalPVSum.sum * recWeight) / 1000
    ),
    fall: Math.round(
      (seasonalRecAveragePrice.fall * seasonalPVSum.fall * recWeight) / 1000
    ),
    win: Math.round(
      (seasonalRecAveragePrice.win * seasonalPVSum.win * recWeight) / 1000
    ),
  }
}

export const calculateSeasonalPvRecRevenue = (params: {
  customer?: PvCustomerForRevenue
  seasonalPVSum: ISeasonalNum
  recYear?: string | number
  seasonalRecAveragePrice?: ISeasonalNum
}): ISeasonalNum =>
  calculateSeasonalSpotRecRevenue(
    params.customer,
    params.seasonalPVSum,
    'energyAndRec',
    params.recYear,
    params.seasonalRecAveragePrice
  )

export const calculateSeasonalPvPreviousRevenue = (params: {
  customer?: PvCustomerForRevenue
  usingPvData: ITimeDataItem[]
  seasonalPVSum: ISeasonalNum
  smpData?: ITimeDataItem[]
  seasonMapping: ISeasonMapping
  recYear?: string | number
  seasonalRecAveragePrice?: ISeasonalNum
}): ISeasonalNum | undefined => {
  const {
    customer,
    usingPvData,
    seasonalPVSum,
    smpData = [],
    seasonMapping,
    recYear,
    seasonalRecAveragePrice,
  } = params
  const revenueContractType = getPvRevenueContractType(customer)

  if (isUnitPriceRevenueContract(revenueContractType)) {
    const unitPrice = Number(customer?.contractPrice ?? 0)

    return {
      spr: Math.round(seasonalPVSum.spr * unitPrice),
      sum: Math.round(seasonalPVSum.sum * unitPrice),
      fall: Math.round(seasonalPVSum.fall * unitPrice),
      win: Math.round(seasonalPVSum.win * unitPrice),
    }
  }

  if (usingPvData.length === 0 || smpData.length === 0) return undefined

  const revenueData = multiplyValuesByRealTime(usingPvData, smpData)
  const smpRevenue = calSeasonalSum(revenueData, seasonMapping)
  const recRevenue = calculateSeasonalSpotRecRevenue(
    customer,
    seasonalPVSum,
    getPreviousRevenueRecOpt(revenueContractType),
    recYear,
    seasonalRecAveragePrice
  )

  return {
    spr: smpRevenue.spr + recRevenue.spr,
    sum: smpRevenue.sum + recRevenue.sum,
    fall: smpRevenue.fall + recRevenue.fall,
    win: smpRevenue.win + recRevenue.win,
  }
}

const calculateMonthlySpotRecRevenue = (
  customer: PvCustomerForRevenue | undefined,
  monthlyPVSum: IMonthNum,
  recData?: any[],
  recOpt?: string,
  recYear?: string | number,
  recRegion: PvRecRegion = 'land'
): IMonthNum => {
  const result: IMonthNum = { ...EMPTY_MONTH_NUM }

  if (
    recOpt !== 'energyAndRec' ||
    !recYear ||
    !Array.isArray(recData) ||
    recData.length === 0
  ) {
    return result
  }

  const priceKey = recRegion === 'jeju' ? 'jejuAvgPrc' : 'landAvgPrc'
  const monthlyPriceSum: IMonthNum = { ...EMPTY_MONTH_NUM }
  const monthlyPriceCount: IMonthNum = { ...EMPTY_MONTH_NUM }

  recData.forEach((item: any) => {
    const month = new Date(item.date).getMonth() + 1
    const price = Number(item[priceKey])

    if (month >= 1 && month <= 12 && !Number.isNaN(price)) {
      const key = month as keyof IMonthNum
      monthlyPriceSum[key] += price
      monthlyPriceCount[key] += 1
    }
  })

  const recWeight = Number(customer?.RECWeight ?? 1)

  for (let month = 1; month <= 12; month++) {
    const key = month as keyof IMonthNum
    const averagePrice =
      monthlyPriceCount[key] === 0
        ? 0
        : monthlyPriceSum[key] / monthlyPriceCount[key]

    result[key] = Math.round(
      (averagePrice * monthlyPVSum[key] * recWeight) / 1000
    )
  }

  return result
}

export const calculateMonthlyPvRecRevenue = (params: {
  customer?: PvCustomerForRevenue
  monthlyPVSum: IMonthNum
  recData?: any[]
  recYear?: string | number
  recRegion?: PvRecRegion
}): IMonthNum =>
  calculateMonthlySpotRecRevenue(
    params.customer,
    params.monthlyPVSum,
    params.recData,
    'energyAndRec',
    params.recYear,
    params.recRegion
  )

export const calculateMonthlyPvPreviousRevenue = (params: {
  customer?: PvCustomerForRevenue
  usingPvData: ITimeDataItem[]
  monthlyPVSum: IMonthNum
  smpData?: ITimeDataItem[]
  recData?: any[]
  recYear?: string | number
  recRegion?: PvRecRegion
}): IMonthNum | undefined => {
  const {
    customer,
    usingPvData,
    monthlyPVSum,
    smpData = [],
    recData,
    recYear,
    recRegion,
  } = params
  const revenueContractType = getPvRevenueContractType(customer)

  if (isUnitPriceRevenueContract(revenueContractType)) {
    const unitPrice = Number(customer?.contractPrice ?? 0)
    const result: IMonthNum = { ...EMPTY_MONTH_NUM }

    for (let month = 1; month <= 12; month++) {
      const key = month as keyof IMonthNum
      result[key] = Math.round(monthlyPVSum[key] * unitPrice)
    }

    return result
  }

  if (usingPvData.length === 0 || smpData.length === 0) return undefined

  const revenueData = multiplyValuesByRealTime(usingPvData, smpData)
  const smpRevenue = calMonthSum(revenueData)
  const recRevenue = calculateMonthlySpotRecRevenue(
    customer,
    monthlyPVSum,
    recData,
    getPreviousRevenueRecOpt(revenueContractType),
    recYear,
    recRegion
  )

  return IMonthNumPlusIMonthNum(smpRevenue, recRevenue)
}
